/**
 * SystemCheckPage — B2B-Systemprüfung für Kunden.
 *
 * Idee: Der Kunde wählt ein konkretes System (z. B. „HS 9 PV"), gibt seine
 * fertigen Strings oder Modulfelder ein, und das Tool prüft, ob das gewählte
 * System mit dieser PV-Konfiguration sauber läuft. Wenn nein → Erklärung,
 * warum nicht, + Alternativen aus dem KUNDEN-EIGENEN Shop-Sortiment.
 *
 * Reuses pv-sizing.js (`tryExhaustiveAssignment` indirekt via
 * `findBestPvSystemSolutions`) — keine Logik-Duplikation.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Info, AlertTriangle,
  CheckCircle2, XCircle, List, LayoutGrid, Upload, Loader2, ShieldCheck,
  Download, ShoppingCart, Copy,
} from 'lucide-react';
import {
  findBestPvSystemSolutions, calculateColdVoc, calculateEstimatedVmp,
  calculateStringPower, getOrientationFactor,
} from '../../lib/pv-sizing';
import { exportPvPdf } from '../../lib/pdf-export';
import { pvProductConfig, PV_DEFAULTS } from '../../config/pv-product-config';
import { orientationOptions } from '../../config/defaults';
import { API_BASE } from '../../lib/api-base';
import { toast } from 'sonner';

const STEPS = ['System wählen', 'Modus', 'Daten eingeben', 'Ergebnis'];

const emptyEntry = () => ({
  id: Date.now() + Math.random(),
  stringName: '', fieldName: '',
  orientation: '', moduleType: '',
  moduleCount: '', vocPerModule: '',
  powerWpPerModule: '', nominalCurrentPerModule: '',
  betaVocPercent: '', minDesignTempC: '', iscPerModule: '', vmpPerModule: '',
});

/** Normalisiere Produkt-Titel und Artikelnummer für ein robustes Modell-Match. */
function matchesModel(productTitle, productNumber, model) {
  if (!model) return false;
  const norm = s => String(s || '').toUpperCase().replace(/[®\s\-_./,]/g, '');
  const target = norm(model);
  return norm(productTitle).includes(target) || norm(productNumber).includes(target);
}

/** Diagnostiziere, warum ein bestimmtes System nicht passt — verbose. */
function diagnoseFailure(preparedEntries, mode, system) {
  const totalPvKw = preparedEntries.reduce(
    (sum, e) => sum + (e.moduleCount * e.powerWpPerModule) / 1000,
    0,
  );
  const effectivePvKw = preparedEntries.reduce(
    (sum, e) => sum + ((e.moduleCount * e.powerWpPerModule) / 1000) * getOrientationFactor(e.orientation),
    0,
  );
  const reasons = [];

  // 1) PV-Leistung außerhalb der Toleranz (über 120 % der Empfehlung)
  const maxAllowedPv = system.recommendedPvKw * (1 + PV_DEFAULTS.pvToleranceFilter);
  if (effectivePvKw > maxAllowedPv) {
    reasons.push({
      type: 'pv_too_large',
      message: `PV-Leistung (${effectivePvKw.toFixed(2)} kW effektiv) übersteigt das Maximum dieses Systems (${maxAllowedPv.toFixed(2)} kW = 120 % der Empfehlung von ${system.recommendedPvKw} kW).`,
    });
  } else if (totalPvKw < system.recommendedPvKw * 0.6) {
    reasons.push({
      type: 'pv_too_small',
      message: `PV-Leistung (${totalPvKw.toFixed(2)} kW) liegt deutlich unter der empfohlenen Mindestauslastung (${(system.recommendedPvKw * 0.6).toFixed(2)} kW = 60 % der Empfehlung). Ein kleineres System ist meistens wirtschaftlicher.`,
    });
  }

  // 2) Voc pro String über Tracker-MaxV — NUR im strings-Modus sinnvoll, weil
  //    im fields-Modus die Lib das Feld automatisch in mehrere Strings teilt.
  if (mode === 'strings') {
    const maxTrackerVoc = Math.max(...system.mppts.map(m => m.maxV));
    for (const e of preparedEntries) {
      const coldVoc = calculateColdVoc(e.vocPerModule, e.betaVocPercent, e.minDesignTempC);
      const stringVocCold = e.moduleCount * coldVoc;
      if (stringVocCold > maxTrackerVoc) {
        reasons.push({
          type: 'voc_too_high',
          message: `String „${e.stringName || e.fieldName || 'Unbenannt'}": Voc kalt = ${stringVocCold.toFixed(1)} V übersteigt die maximale MPPT-Spannung (${maxTrackerVoc} V). Reduziere die Modulanzahl pro String.`,
        });
      }
    }
  }

  // 3) Imp / Strom über Tracker-MaxA
  const maxTrackerCurrent = Math.max(...system.mppts.map(m => m.maxA));
  for (const e of preparedEntries) {
    if (e.nominalCurrentPerModule > maxTrackerCurrent) {
      reasons.push({
        type: 'current_too_high',
        message: `String „${e.stringName || e.fieldName || 'Unbenannt'}": Modulstrom Imp = ${e.nominalCurrentPerModule} A übersteigt die maximale Tracker-Stromstärke (${maxTrackerCurrent} A).`,
      });
    }
  }

  // 4) Zu viele Strings für verfügbare Tracker (nur im 'strings'-Mode klar diagnostizierbar)
  if (mode === 'strings' && preparedEntries.length > system.mppts.length) {
    reasons.push({
      type: 'too_many_strings',
      message: `Du hast ${preparedEntries.length} Strings angegeben, aber das System hat nur ${system.mppts.length} MPPT-Tracker. Strings können bei kompatibler Voc/Imp ggf. parallel geschaltet werden — das wird aber bei dieser Konfiguration nicht ausreichen.`,
    });
  }

  return reasons.length > 0 ? reasons : [{
    type: 'generic',
    message: 'Die Konfiguration lässt sich mit diesem System nicht sauber realisieren — vermutlich aufgrund einer Kombination aus String-Anzahl, Voc-Verteilung und Tracker-Gruppen-Limits.',
  }];
}

export default function SystemCheckPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [availableSystems, setAvailableSystems] = useState([]);
  const [shopProducts, setShopProducts] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [selectedModel, setSelectedModel] = useState('');
  const [mode, setMode] = useState(null);
  const [entries, setEntries] = useState([emptyEntry()]);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [moduleVariants, setModuleVariants] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);

  // Shop-Katalog laden + auf PV-Systeme filtern
  useEffect(() => {
    (async () => {
      setLoadingCatalog(true);
      try {
        const r = await authFetch('/api/shop/products');
        if (!r.ok) throw new Error('catalog');
        const d = await r.json();
        const flat = (d.groups || []).flatMap(g => g.products || []);
        setShopProducts(flat);
        // Schnittmenge: PV-Systeme aus pvProductConfig, die in der Whitelist
        // vorkommen UND tatsächlich PV-Tracker haben (Nicht-PV-Systeme wie
        // reine Speicher HS 5 / GS 30 ohne MPPTs werden ignoriert).
        const matched = pvProductConfig.filter(sys =>
          (sys.mppts || []).length > 0 && (sys.recommendedPvKw || 0) > 0
          && flat.some(p => matchesModel(p.title, p.articleNumber, sys.model)),
        );
        setAvailableSystems(matched);
      } catch {
        toast.error('Produktkatalog konnte nicht geladen werden.');
      } finally {
        setLoadingCatalog(false);
      }
    })();
  }, []);

  const updateEntry = useCallback((id, key, val) => {
    setEntries(prev => prev.map(e => e.id === id ? { ...e, [key]: val } : e));
  }, []);
  const addEntry = () => {
    if (entries.length < PV_DEFAULTS.maxStringsInConfigurator) {
      setEntries(prev => [...prev, emptyEntry()]);
    }
  };
  const addEntryWithCopy = () => {
    if (entries.length < PV_DEFAULTS.maxStringsInConfigurator && entries.length > 0) {
      const last = entries[entries.length - 1];
      setEntries(prev => [...prev, {
        ...emptyEntry(),
        // Moduldaten übernehmen — Name + Anzahl + Ausrichtung bleiben leer.
        moduleType: last.moduleType,
        vocPerModule: last.vocPerModule,
        powerWpPerModule: last.powerWpPerModule,
        nominalCurrentPerModule: last.nominalCurrentPerModule,
        betaVocPercent: last.betaVocPercent,
        minDesignTempC: last.minDesignTempC,
        iscPerModule: last.iscPerModule,
        vmpPerModule: last.vmpPerModule,
      }]);
    }
  };
  const removeEntry = (id) => {
    if (entries.length > 1) setEntries(prev => prev.filter(e => e.id !== id));
  };

  // Übernimmt die Werte einer erkannten Modulvariante in die String-Zeile.
  const applyVariantToEntry = (idx, v) => {
    const id = entries[idx].id;
    if (v.moduleType) updateEntry(id, 'moduleType', v.moduleType);
    if (v.vocPerModule) updateEntry(id, 'vocPerModule', String(v.vocPerModule));
    if (v.powerWp) updateEntry(id, 'powerWpPerModule', String(v.powerWp));
    if (v.nominalCurrentImp) updateEntry(id, 'nominalCurrentPerModule', String(v.nominalCurrentImp));
    if (v.vmpPerModule) updateEntry(id, 'vmpPerModule', String(v.vmpPerModule));
    if (v.betaVocPercent) updateEntry(id, 'betaVocPercent', String(v.betaVocPercent));
    if (v.iscPerModule) updateEntry(id, 'iscPerModule', String(v.iscPerModule));
    setModuleVariants(null);
    toast.success(`Werte übernommen: ${v.moduleType || `${v.powerWp} Wp`}`);
  };

  // Datasheet-Upload (wiederverwendet öffentlichen Endpoint).
  // Fix 27.02.2026: Bei mehreren Modulvarianten im PDF (z. B. 400 / 410 / 420 Wp)
  // zeigen wir jetzt ein Auswahl-Dialog — vorher wurde stumm variants[0] genommen.
  const handleModuleUpload = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setModuleVariants(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_BASE}/api/upload/module-datasheet`, {
        method: 'POST', body: formData,
      });
      const data = await res.json();
      if (data.success && data.variants && data.variants.length > 0) {
        if (data.variants.length === 1) {
          applyVariantToEntry(idx, data.variants[0]);
        } else {
          setModuleVariants({ idx, variants: data.variants, fileName: data.fileName });
        }
      } else {
        toast.error(data.error || 'Datenblatt konnte nicht ausgewertet werden.');
      }
    } catch {
      toast.error('Upload fehlgeschlagen — bitte Werte manuell eingeben.');
    } finally { setUploading(false); }
    // File-Input zurücksetzen, damit dasselbe PDF nochmal hochgeladen werden kann
    if (e.target) e.target.value = '';
  };

  const selectedSystem = useMemo(
    () => availableSystems.find(s => s.model === selectedModel),
    [availableSystems, selectedModel],
  );

  const canNext = () => {
    if (step === 0) return !!selectedModel;
    if (step === 1) return !!mode;
    if (step === 2) return entries.every(e => e.moduleCount > 0 && e.vocPerModule > 0
      && e.powerWpPerModule > 0 && e.nominalCurrentPerModule > 0 && e.orientation);
    return false;
  };

  const runCheck = (overrideModel) => {
    const modelToUse = overrideModel || selectedModel;
    const systemToUse = availableSystems.find(s => s.model === modelToUse);
    if (!systemToUse) return;
    const prepared = entries.map((e, idx) => ({
      ...e,
      moduleCount: Number(e.moduleCount),
      vocPerModule: Number(e.vocPerModule),
      powerWpPerModule: Number(e.powerWpPerModule),
      nominalCurrentPerModule: Number(e.nominalCurrentPerModule),
      betaVocPercent: e.betaVocPercent ? Number(e.betaVocPercent) : PV_DEFAULTS.betaVocPercent,
      minDesignTempC: e.minDesignTempC ? Number(e.minDesignTempC) : PV_DEFAULTS.minDesignTempC,
      vmpPerModule: e.vmpPerModule ? Number(e.vmpPerModule) : null,
      fieldName: e.fieldName || e.stringName || `${mode === 'strings' ? 'String' : 'Feld'} ${idx + 1}`,
      stringName: e.stringName || e.fieldName || `${mode === 'strings' ? 'String' : 'Feld'} ${idx + 1}`,
      orientation: e.orientation || 'sued',
    }));

    // Schritt 1: Beste Lösungen finden (alle möglichen Systeme bewerten)
    const all = findBestPvSystemSolutions(prepared, mode, null);

    // Schritt 2: passt das vom Kunden GEWÄHLTE System?
    const chosenSolution = (all.solutions || []).find(sol => sol.system?.model === modelToUse);

    // Schritt 3: Alternativen aus den vom Kunden im Shop verfügbaren Systemen filtern
    const availableModels = new Set(availableSystems.map(s => s.model));
    const alternatives = (all.solutions || [])
      .filter(sol => sol.system?.model !== modelToUse
        && availableModels.has(sol.system?.model));

    if (chosenSolution) {
      setResult({ status: 'ok', chosen: chosenSolution, alternatives, prepared, model: modelToUse });
    } else {
      const reasons = diagnoseFailure(prepared, mode, systemToUse);
      setResult({ status: 'fail', reasons, alternatives, prepared, model: modelToUse });
    }
  };

  const goNext = () => {
    if (step === 2) { runCheck(); }
    setStep(s => Math.min(s + 1, 3));
  };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const reset = () => {
    setStep(0); setSelectedModel(''); setMode(null);
    setEntries([emptyEntry()]); setResult(null);
  };

  // ── PDF-Export: nutzt die gleiche Funktion wie der öffentliche
  //    PV-Konfigurator (`exportPvPdf`). Wir bauen ein result-Objekt im
  //    erwarteten Format (mit `solutions` = [chosen, ...alternatives]).
  const downloadPdf = () => {
    if (!result || result.status !== 'ok' || !result.chosen) return;
    const totalPvKw = result.prepared.reduce(
      (sum, e) => sum + (e.moduleCount * e.powerWpPerModule) / 1000, 0,
    );
    const solutions = [result.chosen, ...(result.alternatives || []).slice(0, 1)];
    exportPvPdf({ totalPvKw: Number(totalPvKw.toFixed(2)), solutions, warnings: [] });
  };

  // ── In Warenkorb: das im Shop-Katalog hinterlegte Produkt zu diesem
  //    Modell finden und 1× zum Cart hinzufügen. Bei Erfolg → Navigation
  //    zum Produktkatalog, damit der Kunde optional weitere Artikel ergänzen
  //    kann (Zubehör, Kabel, etc.).
  const addToCart = async () => {
    if (!result || result.status !== 'ok') return;
    const product = shopProducts.find(p =>
      matchesModel(p.title, p.articleNumber, result.model),
    );
    if (!product) {
      toast.error('Artikel zum gewählten Modell konnte nicht im Sortiment gefunden werden.');
      return;
    }
    setAddingToCart(true);
    // Snapshot der Systemprüfung mitschicken — der Kunde sieht später in
    // seiner Bestellung, mit welchen Modul-Daten und welcher Tracker-
    // Belegung dieses System ausgelegt wurde.
    const snapshot = {
      mode,  // 'strings' | 'fields'
      checkedAt: new Date().toISOString(),
      entries: result.prepared.map(e => ({
        name: e.fieldName || e.stringName || '',
        orientation: e.orientation,
        moduleType: e.moduleType || null,
        moduleCount: e.moduleCount,
        vocPerModule: e.vocPerModule,
        powerWpPerModule: e.powerWpPerModule,
        nominalCurrentPerModule: e.nominalCurrentPerModule,
        betaVocPercent: e.betaVocPercent,
        vmpPerModule: e.vmpPerModule,
      })),
      // Tracker-Belegung kompakt für die Anzeige
      assignment: (result.chosen?.assignment?.trackers || []).map(t => ({
        id: t.id,
        strings: (t.assigned || []).map(s => ({
          name: s.displayName || s.stringName,
          moduleCount: s.moduleCount,
          powerKw: s.powerKw,
          stringVocCold: s.stringVocCold,
        })),
      })),
      summary: {
        totalModules: result.prepared.reduce((sum, e) => sum + Number(e.moduleCount || 0), 0),
        totalPvKw: result.prepared.reduce(
          (sum, e) => sum + (e.moduleCount * e.powerWpPerModule) / 1000, 0,
        ),
      },
    };
    const r = await authFetch('/api/shop/orders/draft/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        articleId: product.articleId, articleNumber: product.articleNumber,
        title: product.title, description: product.description,
        groupName: product.groupName, quantity: 1,
        single_price_net: product.single_price_net, vat_percent: product.vat_percent,
        unit: product.unit,
        systemCheck: snapshot,
      }),
    });
    setAddingToCart(false);
    if (r.ok) {
      toast.success(`${result.model} wurde in den Warenkorb gelegt.`);
      navigate('/dashboard/meine-bestellungen');
    } else {
      toast.error('In den Warenkorb fehlgeschlagen.');
    }
  };

  return (
    <div className="space-y-6" data-testid="system-check-page">
      <div>
        <h1 className="text-2xl font-bold text-[#32373c] flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#9ec043]" />
          Systemprüfung
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Prüfe, ob deine PV-Konfiguration zu einem unserer HANS-Systeme passt — inklusive Anschlussbelegung und Alternativ-Empfehlung.
        </p>
      </div>

      {/* Steps */}
      <div className="flex items-center gap-2 text-xs">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold text-[10px]
              ${i < step ? 'bg-[#9ec043] text-white' : i === step ? 'bg-[#32373c] text-white' : 'bg-gray-100 text-gray-400'}`}>
              {i + 1}
            </div>
            <span className={i === step ? 'text-[#32373c] font-medium' : 'text-gray-400'}>{label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300" />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {/* Step 0 — System wählen */}
        {step === 0 && (
          <div data-testid="step-select-system">
            <h2 className="text-base font-semibold text-[#32373c] mb-1">Welches System möchtest du prüfen?</h2>
            <p className="text-xs text-gray-500 mb-4">Auswahl aus den für dich freigegebenen PV-Systemen.</p>
            {loadingCatalog ? (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Lade verfügbare Systeme…
              </div>
            ) : availableSystems.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  In deinem Sortiment ist aktuell kein HANS PV-System hinterlegt.
                  Wende dich an order@ees-energiespeicher.de, um dein Sortiment erweitern zu lassen.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="system-grid">
                {availableSystems.map(sys => {
                  const product = shopProducts.find(p => matchesModel(p.title, p.articleNumber, sys.model));
                  return (
                    <button
                      key={sys.model}
                      onClick={() => setSelectedModel(sys.model)}
                      className={`text-left rounded-lg border-2 p-4 transition-colors
                        ${selectedModel === sys.model
                          ? 'border-[#9ec043] bg-[#9ec043]/5'
                          : 'border-gray-200 hover:border-[#9ec043]/40 bg-white'}`}
                      data-testid={`btn-system-${sys.model.replace(/\s+/g, '-')}`}
                    >
                      <div className="font-semibold text-[#32373c]">{sys.model}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        PV bis {sys.recommendedPvKw.toFixed(1)} kWp · {sys.mppts.length} MPPT · Speicher {sys.standardUsableKwh.toFixed(2)} kWh
                      </div>
                      {product && (
                        <div className="text-[10px] text-gray-400 mt-1.5 font-mono">
                          Art.-Nr. {product.articleNumber || '–'}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Step 1 — Eingabemodus */}
        {step === 1 && (
          <div data-testid="step-mode">
            <h2 className="text-base font-semibold text-[#32373c] mb-1">Wie hast du deine PV-Anlage geplant?</h2>
            <p className="text-xs text-gray-500 mb-4">Wähle, ob du fertige Strings oder Modulfelder eingibst.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setMode('strings')}
                className={`text-left rounded-lg border-2 p-4 transition-colors flex items-start gap-3
                  ${mode === 'strings' ? 'border-[#9ec043] bg-[#9ec043]/5' : 'border-gray-200 hover:border-[#9ec043]/40'}`}
                data-testid="btn-mode-strings"
              >
                <List className="w-6 h-6 text-[#9ec043] shrink-0" />
                <div>
                  <div className="font-semibold text-[#32373c]">Strings bekannt</div>
                  <div className="text-xs text-gray-500 mt-0.5">Ich kenne meine fertigen PV-Strings und deren Konfiguration.</div>
                </div>
              </button>
              <button
                onClick={() => setMode('fields')}
                className={`text-left rounded-lg border-2 p-4 transition-colors flex items-start gap-3
                  ${mode === 'fields' ? 'border-[#9ec043] bg-[#9ec043]/5' : 'border-gray-200 hover:border-[#9ec043]/40'}`}
                data-testid="btn-mode-fields"
              >
                <LayoutGrid className="w-6 h-6 text-[#9ec043] shrink-0" />
                <div>
                  <div className="font-semibold text-[#32373c]">Modulfelder bekannt</div>
                  <div className="text-xs text-gray-500 mt-0.5">Ich kenne die Modulfelder/Dachflächen — Stringaufteilung soll vorgeschlagen werden.</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Dateneingabe */}
        {step === 2 && (
          <div data-testid="step-entries">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-base font-semibold text-[#32373c]">
                  {mode === 'strings' ? 'String-Daten' : 'Modulfeld-Daten'} eingeben
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Werte aus dem Modul-Datenblatt — oder PDF-Upload nutzen.
                </p>
              </div>
              <div className="text-xs text-gray-400">
                Gewähltes System: <strong className="text-[#32373c]">{selectedModel}</strong>
              </div>
            </div>
            <div className="space-y-3">
              {entries.map((entry, idx) => (
                <div key={entry.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-[#32373c]">
                      {mode === 'strings' ? 'String' : 'Feld'} {idx + 1}
                    </span>
                    <div className="flex items-center gap-2">
                      <label className={`text-xs cursor-pointer flex items-center gap-1 hover:underline
                        ${uploading ? 'text-gray-400 pointer-events-none' : 'text-[#9ec043]'}`}
                        data-testid={`btn-upload-module-${idx}`}>
                        {uploading
                          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analysiere…</>
                          : <><Upload className="w-3.5 h-3.5" /> Datenblatt</>}
                        <input type="file" accept=".pdf" className="hidden"
                          onChange={e => handleModuleUpload(e, idx)} disabled={uploading} />
                      </label>
                      {entries.length > 1 && (
                        <button onClick={() => removeEntry(entry.id)}
                          className="text-gray-400 hover:text-red-500"
                          data-testid={`btn-remove-entry-${idx}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-gray-500 text-xs">Bezeichnung</Label>
                      <Input
                        value={mode === 'strings' ? entry.stringName : entry.fieldName}
                        onChange={e => updateEntry(entry.id, mode === 'strings' ? 'stringName' : 'fieldName', e.target.value)}
                        placeholder={mode === 'strings' ? 'z. B. Süd-String' : 'z. B. Süddach'}
                        className="mt-1 bg-white text-sm"
                        data-testid={`input-name-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Ausrichtung *</Label>
                      <select
                        value={entry.orientation || ''}
                        onChange={e => updateEntry(entry.id, 'orientation', e.target.value)}
                        className="mt-1 w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
                        data-testid={`select-orientation-${idx}`}>
                        <option value="">Bitte wählen</option>
                        {orientationOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Modultyp</Label>
                      <Input value={entry.moduleType}
                        onChange={e => updateEntry(entry.id, 'moduleType', e.target.value)}
                        placeholder="z. B. JA Solar 420W"
                        className="mt-1 bg-white text-sm"
                        data-testid={`input-module-type-${idx}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-gray-500 text-xs">Anzahl Module *</Label>
                      <Input type="number" min="1" value={entry.moduleCount}
                        onChange={e => updateEntry(entry.id, 'moduleCount', e.target.value)}
                        placeholder="12" className="mt-1 bg-white text-sm"
                        data-testid={`input-module-count-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Voc/Modul (V) *</Label>
                      <Input type="number" step="0.1" value={entry.vocPerModule}
                        onChange={e => updateEntry(entry.id, 'vocPerModule', e.target.value)}
                        placeholder="37.5" className="mt-1 bg-white text-sm"
                        data-testid={`input-voc-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Leistung/Modul (Wp) *</Label>
                      <Input type="number" step="1" value={entry.powerWpPerModule}
                        onChange={e => updateEntry(entry.id, 'powerWpPerModule', e.target.value)}
                        placeholder="420" className="mt-1 bg-white text-sm"
                        data-testid={`input-power-${idx}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-gray-500 text-xs">Nennstrom Imp (A) *</Label>
                      <Input type="number" step="0.01" value={entry.nominalCurrentPerModule}
                        onChange={e => updateEntry(entry.id, 'nominalCurrentPerModule', e.target.value)}
                        placeholder="11.2" className="mt-1 bg-white text-sm"
                        data-testid={`input-current-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Vmp/Modul (V)</Label>
                      <Input type="number" step="0.1" value={entry.vmpPerModule}
                        onChange={e => updateEntry(entry.id, 'vmpPerModule', e.target.value)}
                        placeholder="optional" className="mt-1 bg-white text-sm"
                        data-testid={`input-vmp-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">TK Voc (%/K)</Label>
                      <Input type="number" step="0.01" value={entry.betaVocPercent}
                        onChange={e => updateEntry(entry.id, 'betaVocPercent', e.target.value)}
                        placeholder={String(PV_DEFAULTS.betaVocPercent)}
                        className="mt-1 bg-white text-sm"
                        data-testid={`input-beta-${idx}`} />
                    </div>
                  </div>
                  {entry.moduleCount > 0 && entry.vocPerModule > 0 && entry.powerWpPerModule > 0 && (
                    <div className="text-[11px] text-gray-400 bg-white rounded p-2 grid grid-cols-3 gap-2 border border-gray-100">
                      <span>Voc kalt: {(Number(entry.moduleCount) * calculateColdVoc(Number(entry.vocPerModule), entry.betaVocPercent ? Number(entry.betaVocPercent) : null, entry.minDesignTempC ? Number(entry.minDesignTempC) : null)).toFixed(1)} V</span>
                      <span>Vmp est.: {(Number(entry.moduleCount) * calculateEstimatedVmp(Number(entry.vocPerModule), entry.vmpPerModule ? Number(entry.vmpPerModule) : null)).toFixed(1)} V</span>
                      <span>Leistung: {calculateStringPower(Number(entry.moduleCount), Number(entry.powerWpPerModule)).toFixed(2)} kW</span>
                    </div>
                  )}
                </div>
              ))}

              {/* Modul-Variante auswählen (bei Datenblatt mit mehreren Leistungsklassen) */}
              {moduleVariants && (
                <div className="rounded-lg border-2 border-[#9ec043] bg-[#f8faf2] p-4 space-y-3" data-testid="variant-selection">
                  <h4 className="text-sm font-semibold text-[#32373c]">Modultyp auswählen</h4>
                  <p className="text-xs text-gray-500">
                    Im Datenblatt wurden {moduleVariants.variants.length} Varianten gefunden.
                    Bitte wählen Sie den gewünschten Typ für {mode === 'strings' ? 'String' : 'Feld'} {moduleVariants.idx + 1}:
                  </p>
                  <div className="space-y-2">
                    {moduleVariants.variants.map((v, vi) => (
                      <button
                        key={vi}
                        type="button"
                        onClick={() => applyVariantToEntry(moduleVariants.idx, v)}
                        className="w-full text-left rounded-md border border-gray-200 bg-white p-3 hover:border-[#9ec043] hover:bg-[#f8faf2] transition-colors"
                        data-testid={`variant-option-${vi}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[#32373c]">{v.moduleType || `Variante ${vi + 1}`}</span>
                          <span className="text-xs text-[#9ec043] font-semibold">{v.powerWp} Wp</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          Voc: {v.vocPerModule ?? '–'} V · Vmp: {v.vmpPerModule ?? '–'} V · Imp: {v.nominalCurrentImp ?? '–'} A · TK Voc: {v.betaVocPercent ?? '–'} %/K
                        </div>
                      </button>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setModuleVariants(null)}
                    className="text-xs text-gray-400 underline"
                    data-testid="btn-cancel-variant"
                  >
                    Abbrechen
                  </button>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={addEntry}
                  disabled={entries.length >= PV_DEFAULTS.maxStringsInConfigurator}
                  data-testid="btn-add-entry">
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {mode === 'strings' ? 'String' : 'Feld'} hinzufügen
                </Button>
                {entries.length > 0 && (
                  <Button variant="outline" size="sm" onClick={addEntryWithCopy}
                    disabled={entries.length >= PV_DEFAULTS.maxStringsInConfigurator}
                    className="text-[#9ec043] border-[#9ec043]/30 hover:bg-[#f0f7e0]"
                    data-testid="btn-add-entry-copy">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Moduldaten übernehmen
                  </Button>
                )}
              </div>
              <div className="text-[11px] text-gray-400 flex items-start gap-1.5">
                <Info className="w-3 h-3 mt-0.5 shrink-0 text-[#9ec043]" />
                Bis zu {PV_DEFAULTS.maxStringsInConfigurator} {mode === 'strings' ? 'Strings' : 'Felder'}. Bei Sonderkonfigurationen wende dich an order@ees-energiespeicher.de.
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Ergebnis */}
        {step === 3 && result && (
          <div data-testid="step-result" className="space-y-4">
            {result.status === 'ok' ? (
              <>
                <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="result-ok">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-emerald-900">
                      {result.model} passt zu deiner Konfiguration
                    </div>
                    <div className="text-sm text-emerald-800 mt-1">
                      Stringaufteilung und Anschlussbelegung sind unten aufgeführt.
                    </div>
                  </div>
                </div>

                {/* Anschlussbelegung */}
                <div className="rounded-lg border border-gray-200">
                  <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs font-semibold text-[#32373c]">
                    Anschlussbelegung
                  </div>
                  <div className="p-4 space-y-2.5">
                    {(result.chosen.assignment.trackers || []).map(t => (
                      <div key={t.id} className="grid grid-cols-[60px_1fr] gap-3 items-start" data-testid={`tracker-${t.id}`}>
                        <div className="font-mono text-xs font-semibold text-[#9ec043] tabular-nums">{t.id}</div>
                        <div>
                          {(!t.assigned || t.assigned.length === 0) ? (
                            <span className="text-xs text-gray-400 italic">— frei —</span>
                          ) : (
                            <div className="space-y-0.5">
                              {t.assigned.map((s, i) => (
                                <div key={i} className="text-xs text-gray-700">
                                  <span className="font-medium">{s.displayName || s.stringName}</span>
                                  {' · '}{s.moduleCount} Module · {(s.powerKw || 0).toFixed(2)} kWp
                                  {s.stringVocCold && <> · Voc kalt {s.stringVocCold.toFixed(0)} V</>}
                                  {t.assigned.length > 1 && <span className="text-[10px] text-purple-500 ml-1">(parallel)</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Aktionen: PDF + Warenkorb */}
                <div className="flex flex-wrap gap-2" data-testid="ok-actions">
                  <Button
                    onClick={addToCart}
                    disabled={addingToCart}
                    className="bg-[#9ec043] hover:bg-[#7ca735] text-white"
                    data-testid="btn-add-system-to-cart">
                    {addingToCart
                      ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      : <ShoppingCart className="w-4 h-4 mr-1" />}
                    {result.model} in den Warenkorb
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadPdf}
                    className="text-gray-600 border-gray-300"
                    data-testid="btn-download-pdf">
                    <Download className="w-4 h-4 mr-1" /> Als PDF speichern
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-lg border-2 border-red-200 bg-red-50 p-4 flex items-start gap-3" data-testid="result-fail">
                  <XCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-red-900">
                      {result.model} passt nicht zu deiner Konfiguration
                    </div>
                    <div className="text-sm text-red-800 mt-1">
                      Gründe:
                    </div>
                    <ul className="text-sm text-red-800 mt-1.5 space-y-1 list-disc list-inside">
                      {result.reasons.map((r, i) => <li key={i} data-testid={`fail-reason-${i}`}>{r.message}</li>)}
                    </ul>
                  </div>
                </div>
              </>
            )}

            {/* Alternativen aus dem eigenen Sortiment */}
            {result.alternatives.length > 0 && (
              <div className="rounded-lg border border-[#9ec043]/40 bg-[#9ec043]/5 p-4" data-testid="alternatives-block">
                <div className="text-sm font-semibold text-[#32373c] mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#9ec043]" />
                  {result.status === 'ok' ? 'Weitere passende Systeme aus deinem Sortiment' : 'Empfohlene Alternativen aus deinem Sortiment'}
                </div>
                <div className="space-y-2">
                  {result.alternatives.slice(0, 3).map(alt => (
                    <div key={alt.system.model} className="bg-white rounded border border-gray-200 px-3 py-2 flex items-center justify-between" data-testid={`alt-${alt.system.model.replace(/\s+/g, '-')}`}>
                      <div>
                        <div className="text-sm font-semibold text-[#32373c]">{alt.system.model}</div>
                        <div className="text-xs text-gray-500">
                          PV bis {alt.system.recommendedPvKw.toFixed(1)} kWp · {alt.system.mppts.length} MPPT · Speicher {alt.system.standardUsableKwh.toFixed(2)} kWh
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedModel(alt.system.model);
                          // Direkt mit dem neuen Modell prüfen — sonst hat der
                          // Closure noch das alte selectedModel.
                          runCheck(alt.system.model);
                        }}
                        className="text-xs px-2.5 py-1 rounded-md bg-[#9ec043] text-white hover:bg-[#7ca735]"
                        data-testid={`btn-try-alt-${alt.system.model.replace(/\s+/g, '-')}`}>
                        Diese Alternative prüfen
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.status === 'fail' && result.alternatives.length === 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  Aus deinem aktuellen Sortiment passt leider kein System zu dieser PV-Konfiguration.
                  Wir empfehlen, deine Konfiguration anzupassen (z. B. Modulanzahl pro String reduzieren)
                  oder Kontakt mit uns aufzunehmen — order@ees-energiespeicher.de.
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Buttons */}
      <div className="flex items-center justify-between">
        {step > 0 && step < 3 ? (
          <Button variant="outline" onClick={goBack} data-testid="btn-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
        ) : <span />}
        {step < 3 ? (
          <Button onClick={goNext} disabled={!canNext()} data-testid="btn-next"
            className="bg-[#9ec043] hover:bg-[#7ca735]">
            {step === 2 ? 'Prüfen' : 'Weiter'} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button variant="outline" onClick={reset} data-testid="btn-restart">
            Neue Prüfung starten
          </Button>
        )}
      </div>
    </div>
  );
}
